function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table as RBTable } from 'react-bootstrap';
import { AddButton, DeleteButton, EditButton } from '@rd-web-markets/shared/dist/util/buttons';
import TextFilter from './TextFilter';
import SelectFilter from './SelectFilter';
import { useTranslation } from 'react-i18next';
import tableMethods from './tableMethods';

/**
 * TODO: REFACTOR
 * 
 * @param Array headers
 *  - E.g. [
 *      { text: 'Header 1' sortable: true },
 *      { text: 'Header 2' } // this one is not sortable
 *    ]
 * @param Array rows
 *  - E.g. { columns: [1123, 'Rado' ]}
 * @param Boolean showControls default = true
 *  - Whether to show edit / delete buttons per row.
 * @param Function onEdit
 * @param Function onDelete
 * @param Function onSort (headerIndex, sortOrder) => { ... }
 *
 * Renders a Table.
 */
export default function Table(_ref) {
  let {
    style,
    headers,
    rows,
    showControls = true,
    onEdit,
    onDelete,
    onSort,
    onFilter,
    // required if we want backend text filtering
    textFilterQueryStringParamName,
    // required if we want backend text filtering
    onAddRow = null,
    rowHorizontalPadding,
    // 1|2|3|4|5
    disableDelete,
    isImmutableUnderReview = false,
    displayAddRowBelow = true,
    addRowTextKey = 'add_row',
    tableTdStyle = {},
    rowStyle = {},
    headerStyle = {},
    rowTdStyle = {},
    //recieves current header row height. 
    // so it can be used to determine what is the header row height after render
    // see ProratasSection for an example
    onHeaderRendered = () => {},
    opts = {
      bordered: null,
      striped: null,
      responsive: true,
      hover: true,
      headerSticky: false,
      headersClass: null
    }
  } = _ref;
  const [textFilters, setTextFilters, mapHeaders] = tableMethods.useTextFiltering(textFilterQueryStringParamName);
  const {
    t
  } = useTranslation();
  const [tableRows, setTableRows] = useState([...rows]);
  const [tableHeaders, setTableHeaders] = useState(mapHeaders(headers));
  // If we open the page and the url already has queries, we use these initial texts
  // to fill the inputs with the values from the query string.
  // Required if we have backend text filtering.
  const [initialFilterTexts, setInitialFilterTexts] = useState({});
  const headerRowRef = useRef(null);
  if (tableHeaders.some(h => h.sortable)) {
    if (tableRows.some(r => !r.key)) {
      throw new Error('If the table has sortable headers, its rows must have a key field and this key should be a unique value based on the rendered data.');
    }
  }
  const filterRowsByText = tableMethods.useTextFilteringMethod({
    tableHeaders,
    setTableHeaders,
    rows,
    setTableRows,
    textFilters,
    setTextFilters,
    onFilter
  });
  const columnSortFunction = onSort ? onSort : tableMethods.useSortRows({
    tableHeaders,
    tableRows,
    setTableRows
  });
  const optsDefaults = {
    bordered: null,
    striped: null,
    responsive: true,
    hover: true,
    headerSticky: false
  };
  const optsMerged = {
    ...optsDefaults,
    ...opts
  };
  const headersClass = optsMerged.headersClass ? optsMerged.headersClass : 'text-primary app-fs-table-header ' + (optsMerged.headerSticky ? 'sticky' : '');
  const tableOpts = {
    ...optsMerged
  };
  delete tableOpts.headerClass; // removes errors for unexpected attribute names
  delete tableOpts.headersClass; // removes errors for unexpected attribute names
  delete tableOpts.headerSticky; // removes errors for unexpected attribute names
  delete tableOpts.headerStyle; // removes errors for unexpected attribute names

  function measureHeader() {
    if (headerRowRef.current) {
      const columnWidths = Array.from(headerRowRef.current.querySelectorAll('th')).map(c => c.offsetWidth);
      const headerRowHeight = headerRowRef.current.offsetHeight;
      return {
        columnWidths,
        headerRowHeight
      };
    }
    return {};
  }
  useEffect(() => {
    if (headerRowRef.current) {
      const headerMeasurements = measureHeader();
      onHeaderRendered(headerMeasurements);
    }
  }, [onHeaderRendered]);

  // Use this to do calculations on window resize.
  // The use case for this was that I had to create expandable rows with nested tables, where the nested table
  // had to have exactly the same column widhts as the parent table. However this required to make both tables have
  // their table layout set to "fixed". This meant that they would not update their sizes based on their data.
  // However that meant that the child table specifically does not update itself on resizing of the window,
  // because its widths are set from the original parent table widths. For such cases we call onRenderHeader
  // again when the window is resized, and the table is rerendered. Check the claim group ClaimState on Belgium.
  useEffect(() => {
    const handleResize = () => {
      const headerMeasurements = measureHeader();
      onHeaderRendered(headerMeasurements);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [onHeaderRendered]);
  useEffect(() => {
    setTableRows([...rows]);
    setTableHeaders(mapHeaders(headers));
  }, [rows, headers, mapHeaders]);
  const setTableHeadersSortOrder = index => {
    tableHeaders[index].order = tableHeaders[index].order === 'asc' ? 'desc' : 'asc';
    tableHeaders.forEach((h, i) => {
      if (i !== index) {
        h.order = null;
      }
    });
    setTableHeaders([...tableHeaders]);
    return tableHeaders[index].order;
  };
  const renderSortableHeaderIcon = header => {
    if (!header.order) {
      return /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "sort");
    }
    return header.order === 'asc' ? /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "arrow_drop_up") : /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "arrow_drop_down");
  };
  const renderHeader = (header, index) => {
    const headerClassName = (optsMerged.bordered ? '' : 'border-0') + opts.headerClass ? opts.headerClass : ' font-weight-normal';
    header.attributes ||= {};
    const finalHeaderStyle = {
      ...{
        position: 'relative',
        ...optsMerged.headerStyle,
        verticalAlign: 'text-top',
        width: header.width,
        minWidth: header.width,
        ...header.styleAttributes
      },
      // default,
      ...header.attributes.style
    };
    header.attributes.style = finalHeaderStyle;
    return header.sortable ? /*#__PURE__*/React.createElement("th", _extends({
      key: index,
      onClick: () => {
        setTableHeadersSortOrder(index);
        if (typeof columnSortFunction === 'function') {
          columnSortFunction(index, tableHeaders[index].order, header.text);
        }
      },
      className: headerClassName
    }, header.attributes), /*#__PURE__*/React.createElement("div", {
      className: "cursor-pointer",
      style: {
        marginBottom: '40px'
      }
    }, header.text, " ", renderSortableHeaderIcon(header)), header.filterableByText && /*#__PURE__*/React.createElement(TextFilter, {
      initialValue: header._filterText,
      style: {
        position: 'absolute',
        bottom: 10
      },
      onFilter: text => filterRowsByText(text, index)
    }), header.filterableBySelect && /*#__PURE__*/React.createElement(SelectFilter, {
      initialValue: header._filterText,
      style: {
        position: 'absolute',
        bottom: 10
      },
      onFilter: text => filterRowsByText(text, index),
      options: header.filterOptions
    })) : /*#__PURE__*/React.createElement("th", _extends({
      key: index,
      className: headerClassName
    }, header.attributes), header.text, header.filterableByText && /*#__PURE__*/React.createElement(TextFilter, {
      initialValue: header._filterText,
      style: {
        position: 'absolute',
        bottom: 10
      },
      onFilter: text => filterRowsByText(text, index)
    }));
  };
  function renderExpandedContentRow(row, index) {
    const colSpan = (showControls ? 1 : 0) + tableHeaders.length;
    return /*#__PURE__*/React.createElement("tr", {
      className: "table-row-expanded"
    }, /*#__PURE__*/React.createElement("td", {
      colSpan: colSpan
    }, row.renderExpandedContent(row)));
  }
  function renderRow(row, index) {
    return /*#__PURE__*/React.createElement("tr", {
      key: row.key || `row-${index}`,
      className: row.className,
      style: rowStyle
    }, row.columns.map((colText, i) => {
      let tdClass = rowHorizontalPadding && i === 0 ? `pl-${rowHorizontalPadding}` : '';
      tdClass = rowHorizontalPadding && !showControls && i === row.columns.length - 1 ? `pe-${rowHorizontalPadding}` : tdClass;
      return /*#__PURE__*/React.createElement("td", {
        style: rowTdStyle,
        className: tdClass,
        key: `col-${i}`
      }, colText);
    }), showControls && /*#__PURE__*/React.createElement("td", {
      style: {
        minWidth: '90px'
      },
      className: `text-end ${rowHorizontalPadding ? `pe-${rowHorizontalPadding}` : ''}`
    }, row.controls && /*#__PURE__*/React.createElement("div", null, row.controls), !row.controls && onEdit && row.showEdit !== false && /*#__PURE__*/React.createElement(EditButton, {
      isImmutableUnderReview: isImmutableUnderReview,
      onClick: () => onEdit(index, row)
    }), !row.controls && onDelete && row.showDelete !== false && /*#__PURE__*/React.createElement(DeleteButton, {
      isImmutableUnderReview: isImmutableUnderReview,
      onClick: () => onDelete(index, row),
      disabled: disableDelete
    })));
  }
  const rowsWithExpandedRows = [];
  tableRows.forEach(row => {
    rowsWithExpandedRows.push(row);
    if (row.expandable && row.expanded) {
      rowsWithExpandedRows.push({
        ...row,
        key: `${row.key}-expanded`,
        isExpandedContentRow: true,
        renderExpandedContent: row.renderExpandedContent
      });
    }
  });
  return /*#__PURE__*/React.createElement(RBTable, _extends({}, tableOpts, {
    style: {
      borderCollapse: 'collapse',
      ...style
    }
  }), /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", {
    className: headersClass,
    ref: headerRowRef,
    style: headerStyle
  }, tableHeaders.map((header, i) => renderHeader(header, i)))), /*#__PURE__*/React.createElement("tbody", null, onAddRow && !displayAddRowBelow && /*#__PURE__*/React.createElement("tr", {
    className: "row-add_row"
  }, /*#__PURE__*/React.createElement("td", {
    colSpan: `${tableRows[0]?.columns?.length + 1}`,
    style: tableTdStyle
  }, /*#__PURE__*/React.createElement(AddButton, {
    variant: "link",
    isImmutableUnderReview: isImmutableUnderReview,
    onClick: onAddRow,
    text: t(addRowTextKey)
  }))), rowsWithExpandedRows.map((row, index) => row.isExpandedContentRow ? renderExpandedContentRow(row, index) : renderRow(row, index)), onAddRow && displayAddRowBelow && /*#__PURE__*/React.createElement("tr", {
    className: "row-add_row"
  }, /*#__PURE__*/React.createElement("td", {
    colSpan: `${tableRows[0]?.columns?.length}`,
    style: {
      textAlign: 'left',
      background: 'transparent'
    }
  }, /*#__PURE__*/React.createElement(AddButton, {
    variant: "link",
    isImmutableUnderReview: isImmutableUnderReview,
    onClick: onAddRow,
    text: t(addRowTextKey)
  })))));
}